import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import toast from "react-hot-toast";

// =================================================================
// TYPE DEFINITIONS (Matching Updated Backend Schema)
// =================================================================

type PopulatedUser = { _id: string; fullName: string; employeeType: string };
type AssignmentId = string | PopulatedUser | null;



export interface VoiceNote {
  _id: string;
  url: string;
  objectName: string;
  originalName?: string;
  expiresAt: string;
  uploadedAt: string;
}

export interface RemarkEntry {
    _id: string;
    text: string;
    createdAt: string; // ISO string from backend
}

export interface DailyProgress {
  _id: string;
  memberId: string;
  memberName: string;
  piecesClaimed: number;
  dateClaimed: string;
  status: 'In Progress' | 'Completed';
  dateCompleted?: string;
}

export interface AdminNote {
  _id: string;
  userId: PopulatedUser;
  notes: VoiceNote[];
}

export interface EmployeeNote {
  _id: string;
  userId: PopulatedUser;
  notes: VoiceNote[];
}

export interface TakaEntry {
  id: string;
  meters: number;
  createdAt: string;
  warehouse: "mumbai" | "surat";
}

// Base Assignment Interfaces
interface BaseAssignment {
  _id: string;
  assignedAt: string | null;
  remarks: string;
  voiceNotes: VoiceNote[];
  adminVoiceNotes: VoiceNote[];
  workStatus?: "in progress" | "incomplete" | "completed";
}
interface MasterAssignment extends BaseAssignment {
  masterId: PopulatedUser | string | null;
   masterRemark?: RemarkEntry[]; 
}
interface KarigarAssignment extends BaseAssignment {
  karigarId: PopulatedUser | string | null;
}

export interface GroupWorkAssignment extends BaseAssignment {
  groupWorkId: PopulatedUser | string | null;
  dailyProgress: DailyProgress[];
}

interface JobWorkAssignment extends BaseAssignment {
  jobWorkId: PopulatedUser | string | null;
  jobWorkRemark?: RemarkEntry[];
}

export interface OrderDetail {
  _id: string; // From Mongoose sub-document
  sizeSegmentId: { _id: string; segmentName: string; numberOfSets: number };
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  totalPieces: number;
  materialComponents: any[];
  rateComponents: any[];
  masterAssignments?: MasterAssignment[];
  karigarAssignments?: KarigarAssignment[];
  jobWorkAssignments?: JobWorkAssignment[];
  groupWorkAssignments?: GroupWorkAssignment[];
}

// TrackingData at the top level is now simpler
export interface TrackingData {
  takaArrival: TakaEntry[];
}

export type OrderDataSnapshot = {
  orderId: string | null;
  trackingData: TrackingData;
  orderDetails: OrderDetail[]; // This is the main data to send to backend
};

// =================================================================
// ZUSTAND STORE DEFINITION
// =================================================================

export interface OrderTrackState {
  orderId: string | null;
  lotNumber: string | null;
  designId: any;
  orderDetails: OrderDetail[]; // This will hold the assignments
  totalQuantity: number;
  grandTotalPrice: number;
  trackingData: TrackingData; // Only for Taka
  isDataLoaded: boolean;

  adminNotes: AdminNote[];
  employeeNotes: EmployeeNote[];

  // Actions
  loadOrder: (order: any) => void;
  addTaka: (warehouse: "mumbai" | "surat", meters: number) => void;
  removeTaka: (takaId: string) => void;
  assignEmployeeToSegments: (
    employeeId: string,
    fullName: string,
    employeeType: "Master" | "Karigar" | "job-work" | "Group Work",
    remarks: string,
    segmentDetailIds: string[]
  ) => void;
  reset: () => void;
  getSavePayload: () => {
    trackingData: TrackingData;
    orderDetails: OrderDetail[];
  };
}

const initialState: Omit<
  OrderTrackState,
  | "loadOrder"
  | "addTaka"
  | "removeTaka"
  | "assignEmployeeToSegments"
  | "reset"
  | "getSavePayload"
> = {
  orderId: null,
  lotNumber: null,
  designId: null,
  orderDetails: [],
  totalQuantity: 0,
  grandTotalPrice: 0,
  isDataLoaded: false,
  trackingData: {
    takaArrival: [],
  },

  adminNotes: [],
  employeeNotes: [],
};

export const useOrderTrackStore = create<OrderTrackState>()(
  immer((set, get) => ({
    ...initialState,

    loadOrder: (order) => {
      set((state) => {
        state.orderId = order._id;
        state.lotNumber = order.lotNumber;
        state.designId = order.designId;
        state.totalQuantity = order.totalQuantity ?? 0;
        state.grandTotalPrice = order.grandTotalPrice ?? 0;

        state.orderDetails = (order.orderDetails || []).map((detail: any) => ({
          ...detail,
          // Default to an empty array [] instead of an empty object {}
          masterAssignments: detail.masterAssignments || [],
          karigarAssignments: detail.karigarAssignments || [],
          jobWorkAssignments: detail.jobWorkAssignments || [],
          groupWorkAssignments: detail.groupWorkAssignments || [],
        }));

        state.trackingData.takaArrival = order.trackingData?.takaArrival || [];

        state.adminNotes = order.adminNotes || [];
        state.employeeNotes = order.employeeNotes || [];
        state.isDataLoaded = true;
      });
    },

    addTaka: (warehouse, meters) => {
      const lotNumber = get().lotNumber;
      if (!lotNumber) return;

      const currentCount = get().trackingData.takaArrival.length;
      const newTakaId = `${lotNumber}-${currentCount + 1}`;

      const newTaka: TakaEntry = {
        id: newTakaId,
        meters,
        createdAt: new Date().toISOString(),
        warehouse,
      };
      set((state) => {
        state.trackingData.takaArrival.push(newTaka);
      });
    },

    removeTaka: (takaId) => {
      set((state) => {
        state.trackingData.takaArrival = state.trackingData.takaArrival.filter(
          (taka) => taka.id !== takaId
        );
      });
    },

    assignEmployeeToSegments: (
      employeeId,
      fullName,
      employeeType,
      remarks,
      segmentDetailIds
    ) => {
      set((state) => {
        let idKey: "masterId" | "karigarId" | "jobWorkId" | "groupWorkId";
        let assignmentArrayKey:
          | "masterAssignments"
          | "karigarAssignments"
          | "jobWorkAssignments"
          | "groupWorkAssignments";

        // Step 1: Employee ke type ke hisab se sahi keys (e.g., masterId) aur array ka naam (e.g., masterAssignments) decide karna.
        if (employeeType === "Master") {
          assignmentArrayKey = "masterAssignments";
          idKey = "masterId";
        } else if (employeeType === "Karigar") {
          assignmentArrayKey = "karigarAssignments";
          idKey = "karigarId";
        } else if (employeeType === "Group Work") {
          assignmentArrayKey = "groupWorkAssignments";
          idKey = "groupWorkId";
        } else {
          // 'job-work'
          assignmentArrayKey = "jobWorkAssignments";
          idKey = "jobWorkId";
        }

        // Step 2: Order ke har ek size segment (detail) par loop chalana.
        state.orderDetails.forEach((detail) => {
          if (!(detail as any)[assignmentArrayKey]) {
            (detail as any)[assignmentArrayKey] = [];
          }
          const assignments = (detail as any)[assignmentArrayKey];

          // Pata lagayein ki current employee is segment mein pehle se assigned hai ya nahi.
          const existingAssignmentIndex = assignments.findIndex(
            (a: any) => (a[idKey]?._id || a[idKey]) === employeeId
          );

          // Step 3: Check karna ki is size ka checkbox selected hai ya nahi.
          if (segmentDetailIds.includes(detail._id)) {
            // --- CASE A: Checkbox CHECKED hai (Assign/Update karna hai) ---

            if (existingAssignmentIndex !== -1) {
              // Agar user pehle se assigned hai, to sirf remarks update karo.
              assignments[existingAssignmentIndex].remarks = remarks;
            } else {
              // Agar user assigned nahi hai, to ek naya assignment object add karo.
              assignments.push({
                _id: `temp_${crypto.randomUUID()}`, // UI ke liye temporary ID. Backend ise save karte waqt aasal ID se badal dega.
                [idKey]: {
                  // Instant UI update ke liye populated object banate hain.
                  _id: employeeId,
                  fullName: fullName,
                  employeeType: employeeType,
                },
                remarks: remarks,
                assignedAt: new Date().toISOString(),
                workStatus: "in progress",
                voiceNotes: [],
                adminVoiceNotes: [],
              });
            }
          } else {


            if (existingAssignmentIndex !== -1) {
              // Agar user pehle se assigned tha, to use is segment se hata do.
              assignments.splice(existingAssignmentIndex, 1);
            }
          }
        });
      });

      toast.success(`Assignments updated for ${employeeType}!`);
    },

    reset: () => {
      set(initialState);
    },

    getSavePayload: () => {
      const state = get();
      // This is the clean payload for our new backend controller
      return {
        trackingData: state.trackingData,
        orderDetails: state.orderDetails,
      };
    },
  }))
);
